import { extend, colord } from 'colord';
import mixPlugin from 'colord/plugins/mix';
import a11yPlugin from 'colord/plugins/a11y';

// https://github.com/omgovich/colord#readme
extend([a11yPlugin, mixPlugin]);
const lighten = (color, amount = 0) => colord(color)
    .lighten(amount / 100)
    .toHex();
const darken = (color, amount = 0) => colord(color)
    .darken(amount / 100)
    .toHex();
const saturate = (color, amount = 0) => colord(color)
    .saturate(amount / 100)
    .toHex();
const desaturate = (color, amount = 0) => colord(color)
    .desaturate(amount / 100)
    .toHex();
const opacify = (color, amount = 0) => colord(color)
    .alpha(amount / 100)
    .toHex();
const invert = (color) => colord(color).invert()
    .toHex();
const rotateHue = (color, degree) => {
    const currentHue = colord(color).hue();
    return colord(color).hue(currentHue + degree)
        .toHex();
};
const isLight = (color) => colord(color).isLight();
const isDark = (color) => colord(color).isDark();
const getHue = (color) => colord(color).hue();
const getHSLA = (color) => colord(color).toHsl();
const checkIsReadable = (backgroundColor, textColor) => colord(textColor).isReadable(backgroundColor, { level: 'AAA' });
const mix = (colorOne, colorTwo, ratio = 50) => colord(colorOne).mix(colorTwo, ratio / 100)
    .toHex();
const setHue = (color, hue) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        ...initialHLSA,
        h: hue,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const setSaturation = (color, saturation) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        ...initialHLSA,
        s: saturation,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const setLightness = (color, lightness) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        ...initialHLSA,
        l: lightness,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const setHSL = (color, hue, saturation, lightness) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        h: hue,
        s: saturation,
        l: lightness,
        a: initialHLSA.a,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const addHue = (color, hue) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        ...initialHLSA,
        h: initialHLSA.h + hue,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const addSaturation = (color, saturation) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        ...initialHLSA,
        s: initialHLSA.s + saturation,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const addLightness = (color, lightness) => {
    const initialHLSA = getHSLA(color);
    const newColor = {
        ...initialHLSA,
        l: initialHLSA.l + lightness,
    };
    return colord(`hsl(${newColor.h}, ${newColor.s}%, ${newColor.l}%)`).toHex();
};
const lumin = (color, amount) => {
    if (amount > 0) {
        return lighten(color, amount);
    }
    return darken(color, Math.abs(amount));
};
const toRGBA = (color) => {
    const rgba = colord(color).toRgb();
    return {
        r: rgba.r,
        g: rgba.g,
        b: rgba.b,
        a: rgba.a,
    };
};
const generateColor = (config) => {
    let tempColor = config.color;
    if (config.hue) {
        tempColor = rotateHue(tempColor, config.hue);
    }
    if (config.saturation) {
        if (config.saturation > 0) {
            tempColor = saturate(tempColor, config.saturation);
        }
        else {
            tempColor = desaturate(tempColor, Math.abs(config.saturation));
        }
    }
    if (config.lightness) {
        if (config.lightness > 0) {
            tempColor = lighten(tempColor, config.lightness);
        }
        else {
            tempColor = darken(tempColor, Math.abs(config.lightness));
        }
    }
    if (config.alpha) {
        tempColor = opacify(tempColor, config.alpha);
    }
    if (config.invertColor) {
        tempColor = invert(tempColor);
    }
    return tempColor;
};

export { addHue, addLightness, addSaturation, checkIsReadable, darken, desaturate, generateColor, getHSLA, getHue, invert, isDark, isLight, lighten, lumin, mix, opacify, rotateHue, saturate, setHSL, setHue, setLightness, setSaturation, toRGBA };
